import { Button } from './button'

/**
 * `EmptyState` - "no rows" panel rendered inside `AccountTable`'s tbody
 * override. Two flavours:
 *
 *   - No filter applied -> "No members to show."
 *   - Filter applied    -> "No members match that account number." + Clear.
 *
 * `role="status"` (not `alert`) because empty is a normal state, not an
 * error.
 *
 * ## The message does not echo what was searched for
 *
 * the MUR "Design Tokens and Accessibility" page (content rules): *do not include the searched
 * ext_account_id in a "not found" message verbatim - reference "the account
 * you requested" instead.* This panel used to interpolate the raw filter
 * value, which put a customer account identifier into on-screen copy that
 * outlives the search - it stays rendered while the operator walks away,
 * and it lands in any screenshot or screen-share of the page.
 *
 * The value the user typed is still visible in the filter input directly
 * above, so nothing is lost by naming the field instead of the value.
 * `filterField` says which field was searched; the value is not passed in
 * at all, so it cannot be reintroduced by accident.
 */

export type FilterField = 'account number' | 'legacy id'

export interface EmptyStateProps {
  readonly filtered: boolean
  readonly filterField: FilterField | null
  readonly onClearFilter: () => void
}

export function EmptyState({ filtered, filterField, onClearFilter }: EmptyStateProps): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center gap-murmuration-3 px-murmuration-6 py-murmuration-7 text-center"
    >
      {filtered ? (
        <>
          <p className="text-murmuration-body text-text-primary">
            No members match that {filterField ?? 'filter'}.
          </p>
          <p className="text-murmuration-body text-text-secondary">
            Check the value above, or clear the filter to see all members.
          </p>
          <Button onClick={onClearFilter} className="mt-murmuration-2">
            Clear filter
          </Button>
        </>
      ) : (
        <>
          <p className="text-murmuration-body text-text-primary">No members to show.</p>
          <p className="text-murmuration-body text-text-secondary">
            Filter by account number or legacy id to find a member.
          </p>
        </>
      )}
    </div>
  )
}
